import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Eye, EyeOff, Lock, Mail, User, AtSign, ArrowLeft } from 'lucide-react'
import { LANGUAGES } from '@olimhub/shared'
import type { Language } from '@olimhub/shared'
import { authApi } from '@/api'
import { getAuthError } from '@/lib/auth-error'
import { useAuthStore } from '@/store/auth.store'
import { useUiStore } from '@/store/ui.store'
import { useFeedStore } from '@/store/feed.store'
import logoLight from '@/assets/logo/olimhub-logo.png'
import logoDark from '@/assets/logo/olimhub-logo-dark.png'

const UI_LANGS = LANGUAGES.filter((l) => l !== 'he')
const FEED_LANGS: Language[] = ['en', 'ru', 'pt', 'es']

const USERNAME_RE = /^[a-z0-9_]{3,30}$/

function LangSelector() {
  const { i18n } = useTranslation()
  const currentLang = i18n.language.slice(0, 2)

  return (
    <div
      role="group"
      aria-label="Language"
      style={{
        display: 'inline-flex',
        padding: 3,
        gap: 2,
        background: 'var(--surface-2)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-pill)',
      }}
    >
      {UI_LANGS.map((l) => {
        const on = currentLang === l
        return (
          <button
            key={l}
            type="button"
            onClick={() => {
              void i18n.changeLanguage(l)
              localStorage.setItem('i18nextLng', l)
              document.documentElement.lang = l
              document.documentElement.dir = 'ltr'
            }}
            aria-pressed={on}
            style={{
              border: 'none',
              cursor: 'pointer',
              padding: '5px 12px',
              borderRadius: 'var(--radius-pill)',
              fontFamily: 'var(--font-sans)',
              fontWeight: 700,
              fontSize: 'var(--fs-xs)',
              background: on ? 'var(--brand)' : 'transparent',
              color: on ? 'var(--on-brand)' : 'var(--text-muted)',
              transition: 'background var(--dur-fast) var(--ease-out)',
            }}
          >
            {l.toUpperCase()}
          </button>
        )
      })}
    </div>
  )
}

function PasswordField({
  label,
  placeholder,
  value,
  onChange,
  error,
}: {
  label: string
  placeholder: string
  value: string
  onChange: (v: string) => void
  error?: boolean
}) {
  const [show, setShow] = useState(false)
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <span
        style={{
          fontFamily: 'var(--font-sans)',
          fontWeight: 600,
          fontSize: 'var(--fs-sm)',
          color: 'var(--text-strong)',
        }}
      >
        {label}
      </span>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', minWidth: 0 }}>
        <span
          style={{
            position: 'absolute',
            insetInlineStart: 12,
            color: 'var(--text-muted)',
            display: 'flex',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        >
          <Lock size={18} />
        </span>
        <input
          type={show ? 'text' : 'password'}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required
          style={{
            flex: 1,
            minWidth: 0,
            width: '100%',
            padding: '11px 44px 11px 40px',
            border: '1.5px solid var(--border)',
            borderColor: error ? 'var(--danger)' : 'var(--border)',
            borderRadius: 'var(--radius-md)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-body)',
            background: 'var(--surface)',
            color: 'var(--text-strong)',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
        <button
          type="button"
          aria-label={show ? 'Hide password' : 'Show password'}
          onClick={() => setShow((v) => !v)}
          style={{
            position: 'absolute',
            insetInlineEnd: 0,
            width: 44,
            height: '100%',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--text-muted)',
            borderRadius: '0 var(--radius-md) var(--radius-md) 0',
          }}
        >
          {show ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
    </label>
  )
}

const inputStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  width: '100%',
  padding: '11px 12px 11px 40px',
  border: '1.5px solid var(--border)',
  borderRadius: 'var(--radius-md)',
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--fs-body)',
  background: 'var(--surface)',
  color: 'var(--text-strong)',
  outline: 'none',
  boxSizing: 'border-box',
}

interface FieldErrors {
  displayName?: string
  username?: string
  email?: string
  password?: string
}

export function Signup() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { isAuthenticated, setAuth } = useAuthStore()
  const { theme } = useUiStore()
  const { guestFeedLanguages } = useFeedStore()

  // Read ?from= query param for post-signup redirect — same convention as
  // Login.tsx, so an unregistered visitor who followed a share link (e.g. an
  // Action) lands back on it after creating their account.
  const [searchParams] = useSearchParams()
  const rawFrom = searchParams.get('from')
  const from = rawFrom ? decodeURIComponent(rawFrom) : '/'

  const [displayName, setDisplayName] = useState('')
  const [username, setUsername] = useState('')
  const [usernameError, setUsernameError] = useState<string | null>(null)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [feedLanguages, setFeedLanguages] = useState<Language[]>(guestFeedLanguages)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [loading, setLoading] = useState(false)

  function toggleFeedLang(lang: Language) {
    setFeedLanguages(prev =>
      prev.includes(lang) ? prev.filter(l => l !== lang) : [...prev, lang]
    )
  }

  // Already authenticated → go straight to the original destination
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true })
    }
  }, [isAuthenticated, navigate, from])

  function validateUsername(val: string) {
    if (!USERNAME_RE.test(val)) {
      setUsernameError('Username: 3–30 chars, lowercase letters, numbers, _ only')
    } else {
      setUsernameError(null)
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setFieldErrors({})

    // Inline username validation
    if (!USERNAME_RE.test(username)) {
      setUsernameError('Username: 3–30 chars, lowercase letters, numbers, _ only')
      return
    }

    setLoading(true)
    try {
      const result = await authApi.signup({ email, username, password, displayName: displayName || undefined, feedLanguages })
      setAuth(result.accessToken, result.account)
      navigate(from, { replace: true })
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { details?: Record<string, string[]>; error?: string } } }
      const details = axiosErr?.response?.data?.details
      if (details && typeof details === 'object') {
        const fe: FieldErrors = {}
        for (const field of ['displayName', 'username', 'email', 'password'] as const) {
          const msgs = details[field]
          if (Array.isArray(msgs) && msgs.length > 0) fe[field] = msgs[0]!
        }
        if (Object.keys(fe).length > 0) {
          setFieldErrors(fe)
        } else {
          setError(getAuthError(err, t, 'signup'))
        }
      } else if (axiosErr?.response?.status === 409) {
        const errMsg = axiosErr?.response?.data?.error ?? ''
        if (/email/i.test(errMsg)) {
          setFieldErrors({ email: t('auth.error_email_taken') })
        } else if (/username/i.test(errMsg)) {
          setFieldErrors({ username: t('auth.error_username_taken') })
        } else {
          setError(getAuthError(err, t, 'signup'))
        }
      } else {
        setError(getAuthError(err, t, 'signup'))
      }
    } finally {
      setLoading(false)
    }
  }

  const logo = theme === 'dark' ? logoDark : logoLight
  const dir = i18n.dir()

  return (
    <div
      dir={dir}
      style={{
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--bg)',
      }}
    >
      {/* Scrollable card area */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '20px 20px 32px',
          overflowY: 'auto',
        }}
      >
        {/* Card */}
        <div
          style={{
            width: '100%',
            maxWidth: 'var(--feed-max)',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-card)',
            padding: '28px 20px 24px',
            boxSizing: 'border-box',
          }}
        >
          {/* Logo + spectrum + lang selectors */}
          <div
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, marginBottom: 20 }}
          >
            <Link to="/">
              <img src={logo} alt="OlimHub" style={{ height: 34, width: 'auto', display: 'block' }} />
            </Link>
            <div className="oh-spectrum oh-spectrum--smooth" style={{ width: 52, height: 4 }} />
            <div className="oh-lang-row">
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {t('auth.ui_language')}
                </span>
                <LangSelector />
              </div>
              <div className="oh-lang-row__sep" />
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {t('auth.feed_languages')}
                </span>
                <div
                  role="group"
                  aria-label="Feed languages"
                  style={{ display: 'inline-flex', padding: 3, gap: 2, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)' }}
                >
                  {FEED_LANGS.map(lang => {
                    const on = feedLanguages.includes(lang)
                    return (
                      <button
                        key={lang}
                        type="button"
                        onClick={() => toggleFeedLang(lang)}
                        aria-pressed={on}
                        style={{
                          border: 'none',
                          cursor: 'pointer',
                          padding: '5px 12px',
                          borderRadius: 'var(--radius-pill)',
                          fontFamily: 'var(--font-sans)',
                          fontWeight: 700,
                          fontSize: 'var(--fs-xs)',
                          background: on ? 'var(--brand)' : 'transparent',
                          color: on ? 'var(--on-brand)' : 'var(--text-muted)',
                          transition: 'background var(--dur-fast) var(--ease-out)',
                        }}
                      >
                        {lang.toUpperCase()}
                      </button>
                    )
                  })}
                </div>
                <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', color: 'var(--text-faint)' }}>
                  {t('auth.feed_languages_hint')}
                </span>
              </div>
            </div>
          </div>

          {/* Heading */}
          <div style={{ textAlign: 'center', marginBottom: 22 }}>
            <h1
              style={{
                fontSize: 'var(--fs-h2)',
                fontWeight: 700,
                color: 'var(--text-strong)',
                letterSpacing: '-0.02em',
                margin: 0,
              }}
            >
              {t('auth.signup_title')}
            </h1>
            <p
              style={{
                margin: '8px auto 0',
                maxWidth: '30ch',
                color: 'var(--text-muted)',
                fontSize: 'var(--fs-sm)',
                lineHeight: 'var(--lh-normal)',
              }}
            >
              {t('auth.signup_sub')}
            </p>
          </div>

          {/* Generic top-level error */}
          {error && (
            <p
              style={{
                margin: '0 0 12px',
                fontSize: 'var(--fs-sm)',
                color: 'var(--danger)',
                textAlign: 'center',
                fontFamily: 'var(--font-sans)',
              }}
            >
              {error}
            </p>
          )}

          {/* Form */}
          <form onSubmit={(e) => { void handleSubmit(e) }} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Display name */}
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
              <span
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontWeight: 600,
                  fontSize: 'var(--fs-sm)',
                  color: 'var(--text-strong)',
                }}
              >
                {t('auth.display_name')}
              </span>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center', minWidth: 0 }}>
                <span
                  style={{
                    position: 'absolute',
                    insetInlineStart: 12,
                    color: 'var(--text-muted)',
                    display: 'flex',
                    pointerEvents: 'none',
                    zIndex: 1,
                  }}
                >
                  <User size={18} />
                </span>
                <input
                  type="text"
                  placeholder={t('auth.display_name_placeholder')}
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  style={inputStyle}
                />
              </div>
              {fieldErrors.displayName && (
                <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--danger)', fontFamily: 'var(--font-sans)' }}>
                  {fieldErrors.displayName}
                </span>
              )}
            </label>

            {/* Username */}
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
              <span
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontWeight: 600,
                  fontSize: 'var(--fs-sm)',
                  color: 'var(--text-strong)',
                }}
              >
                {t('auth.username')}
              </span>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center', minWidth: 0 }}>
                <span
                  style={{
                    position: 'absolute',
                    insetInlineStart: 12,
                    color: 'var(--text-muted)',
                    display: 'flex',
                    pointerEvents: 'none',
                    zIndex: 1,
                  }}
                >
                  <AtSign size={18} />
                </span>
                <input
                  type="text"
                  placeholder={t('auth.username_placeholder')}
                  value={username}
                  onChange={(e) => {
                    const val = e.target.value.toLowerCase()
                    setUsername(val)
                    validateUsername(val)
                  }}
                  required
                  style={{
                    ...inputStyle,
                    borderColor: usernameError || fieldErrors.username ? 'var(--danger)' : 'var(--border)',
                  }}
                />
              </div>
              {(usernameError || fieldErrors.username) && (
                <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--danger)', fontFamily: 'var(--font-sans)' }}>
                  {usernameError ?? fieldErrors.username}
                </span>
              )}
            </label>

            {/* Email */}
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
              <span
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontWeight: 600,
                  fontSize: 'var(--fs-sm)',
                  color: 'var(--text-strong)',
                }}
              >
                {t('auth.email')}
              </span>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center', minWidth: 0 }}>
                <span
                  style={{
                    position: 'absolute',
                    insetInlineStart: 12,
                    color: 'var(--text-muted)',
                    display: 'flex',
                    pointerEvents: 'none',
                    zIndex: 1,
                  }}
                >
                  <Mail size={18} />
                </span>
                <input
                  type="email"
                  placeholder={t('auth.email_placeholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  style={{
                    ...inputStyle,
                    borderColor: fieldErrors.email ? 'var(--danger)' : 'var(--border)',
                  }}
                />
              </div>
              {fieldErrors.email && (
                <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--danger)', fontFamily: 'var(--font-sans)' }}>
                  {fieldErrors.email}
                </span>
              )}
            </label>

            <div>
              <PasswordField
                label={t('auth.password')}
                placeholder={t('auth.password_placeholder')}
                value={password}
                onChange={setPassword}
                error={!!fieldErrors.password}
              />
              {fieldErrors.password && (
                <span
                  style={{
                    display: 'block',
                    marginTop: 4,
                    fontSize: 'var(--fs-xs)',
                    color: 'var(--danger)',
                    fontFamily: 'var(--font-sans)',
                  }}
                >
                  {fieldErrors.password}
                </span>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                marginTop: 4,
                padding: '13px 0',
                border: 'none',
                borderRadius: 'var(--radius-pill)',
                background: 'var(--brand)',
                color: 'var(--on-brand)',
                fontFamily: 'var(--font-sans)',
                fontWeight: 700,
                fontSize: 'var(--fs-body)',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
                width: '100%',
              }}
            >
              {loading ? t('common.loading') : t('auth.signup_cta')}
            </button>

            <p
              style={{
                margin: '2px 0 0',
                textAlign: 'center',
                fontSize: 'var(--fs-xs)',
                color: 'var(--text-faint)',
                lineHeight: 'var(--lh-normal)',
                fontFamily: 'var(--font-sans)',
              }}
            >
              {t('auth.terms')}
            </p>
          </form>

          <div style={{ height: 1, background: 'var(--border)', margin: '20px 0 16px' }} />

          <div style={{ textAlign: 'center', fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>
            {t('auth.to_login')}{' '}
            <button
              type="button"
              onClick={() => navigate(rawFrom ? `/login?from=${rawFrom}` : '/login')}
              style={{
                border: 'none',
                background: 'none',
                padding: 0,
                cursor: 'pointer',
                fontFamily: 'var(--font-sans)',
                fontWeight: 700,
                fontSize: 'var(--fs-sm)',
                color: 'var(--brand)',
              }}
            >
              {t('auth.to_login_cta')}
            </button>
          </div>
        </div>

        {/* Back to home */}
        <Link
          to="/"
          style={{
            marginTop: 20,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 'var(--fs-sm)',
            color: 'var(--text-muted)',
            textDecoration: 'none',
            fontFamily: 'var(--font-sans)',
            fontWeight: 500,
          }}
        >
          <ArrowLeft size={16} />
          Back to home
        </Link>

        {/* Static page links */}
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', marginTop: 14 }}>
          {[
            { to: '/about', label: t('nav.about') },
            { to: '/conduct', label: t('nav.conduct') },
            { to: '/privacy', label: t('nav.privacy') },
          ].map(({ to, label }, i) => (
            <span key={to} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {i > 0 && <span style={{ color: 'var(--text-faint)', fontSize: 'var(--fs-xs)' }}>·</span>}
              <Link
                to={to}
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-xs)',
                  color: 'var(--text-muted)',
                  textDecoration: 'none',
                  fontWeight: 500,
                }}
              >
                {label}
              </Link>
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}
